/**
 * KIOSK 機台身分（device_code）— runtime 解析與持久化。
 *
 * 背景：同一份 build 會部署到多台實體購票機。原本 device_code 由 build-time
 * 的 `VITE_KIOSK_DEVICE_CODE` 寫死進 bundle，導致「同一份 build 跑多台 → 全部
 * 撞同一個 code → device_status 互相覆蓋、儀表板分不出誰是誰」。本模組改為
 * **runtime 綁定**：每台機各自在裝置上設定一次，之後持久化沿用。
 *
 * 解析優先序（{@link resolveDeviceCode}，App 啟動時呼叫一次）：
 *   1. URL `?device=CODE` —— 佈建便利；命中即寫入 localStorage 並從網址移除，
 *      之後重整 / 重開沿用，顧客不會看到參數。
 *   2. localStorage('kiosk.device_code') —— 已綁定之機台。
 *   3. `import.meta.env.VITE_KIOSK_DEVICE_CODE` —— dev 預設 / 向後相容。
 *   4. null —— 未綁定；heartbeat 不送，維運可進 /device-setup 設定。
 */

const STORAGE_KEY = 'kiosk.device_code';
const listeners = new Set<() => void>();

function normalize(code: string): string {
  return code.trim();
}

/** 讀 localStorage 綁定值（未綁定 / 不可用回 null）。 */
function readStored(): string | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v !== null && v.trim() !== '' ? v : null;
  } catch {
    return null;
  }
}

/** dev 預設 / 向後相容之 build-time 值（未設回 null）。 */
function envDefault(): string | null {
  const v = import.meta.env.VITE_KIOSK_DEVICE_CODE;
  return v !== undefined && v.trim() !== '' ? v : null;
}

/** 目前綁定之 device_code；localStorage 優先，其次 env 預設，皆無回 null（未綁定）。 */
export function getDeviceCode(): string | null {
  return readStored() ?? envDefault();
}

/** 設定並持久化 device_code（維運綁定 / URL 佈建）；空字串視同解除綁定。 */
export function setDeviceCode(code: string): void {
  const c = normalize(code);
  try {
    if (c === '') window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, c);
  } catch {
    /* localStorage 不可用（隱私模式等）→ 無法持久化，僅忽略 */
  }
  notify();
}

/** 解除綁定（回未綁定狀態；若有 env 預設則 getDeviceCode 仍回該預設）。 */
export function clearDeviceCode(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  notify();
}

/**
 * App 啟動時呼叫一次：消化 URL `?device=` 佈建參數（寫入 localStorage 並從
 * 網址移除），回傳解析後之 device_code（或 null）。
 */
export function resolveDeviceCode(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('device');
    if (fromUrl !== null && normalize(fromUrl) !== '') {
      setDeviceCode(fromUrl);
      params.delete('device');
      const qs = params.toString();
      const url = window.location.pathname + (qs !== '' ? `?${qs}` : '') + window.location.hash;
      window.history.replaceState(null, '', url);
    }
  } catch {
    /* URL / history 不可用 → 略過 */
  }
  return getDeviceCode();
}

function notify(): void {
  for (const l of listeners) l();
}

/** 訂閱 device_code 變更（綁定 / 解除時觸發）；回傳取消訂閱函式。 */
export function subscribeDeviceCode(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
